import { Widget } from './form-widget.model';
import moment from 'moment';
import { parseLocalDateString } from '../helpers/date-utils';

export class FbEmrMedicalHistoryWidget extends Widget {
  constructor(data?: any) {
    super(data);
    this.name = 'fb-emr-medical-history';
    this.title = 'Medical History';

    if (!this.properties.text) {
      this.properties.text = { title: 'Header', value: 'Medical History' };
    }
    if (!this.properties.content) {
      this.properties.content = {
        title: 'Description',
        value: '',
        placeholder: 'Add a description or, leave blank',
      };
    }
    // if (!this.properties.value) {
    //     this.properties.value = { title: 'Value', value: '' };
    // }
    // if (!this.properties.items) {
    //     this.properties.items = {
    //         title: 'Items',
    //         value: [],
    //     };
    // }
  }

  isValid() {
    if (!this.properties?.items?.value) {
      return true;
    }

    const items = this.properties.items.value;
    let isValid = true;

    for (const item of items) {
      if (item.visible === false || !item.type) {
        continue;
      }

      if (item.type === 'input-list' && Array.isArray(item.value) && item.value.length > 0) {
        if (item.value.length === 1 && this.isEmptyInputListRow(item.value[0])) {
          continue;
        }

        for (const row of item.value) {
          if (this.isEmptyInputListRow(row)) {
            continue;
          }

          for (const field of row) {
            if (
              this.shouldValidateRequiredInputField(item, field) &&
              this.isEmptyValue(field.value)
            ) {
              isValid = false;
              return false;
            }
          }
        }
      }

      if (item.type === 'table' && Array.isArray(item.value) && item.value.length > 0) {
        if (item.value.length === 1 && this.isEmptyTableRow(item.value[0], item.items)) {
          continue;
        }

        for (const row of item.value) {
          if (this.isEmptyTableRow(row, item.items)) {
            continue;
          }

          for (const field of item.items) {
            const fieldName = field.title || field.name;
            const fieldValue = row[fieldName];

            if (
              this.shouldValidateRequiredTableField(item, field) &&
              this.isEmptyValue(fieldValue)
            ) {
              isValid = false;
              return false;
            }
          }
        }
      }
    }

    return isValid;
  }

  private isEmptyInputListRow(row: any[]): boolean {
    if (!Array.isArray(row)) return true;
    return row.every((field) => !field.value);
  }

  private isEmptyTableRow(row: any, fields: any[]): boolean {
    if (!row || typeof row !== 'object') return true;

    const valueProps = Object.values(row);

    return valueProps.every((val) => {
      if (val === null || val === undefined || val === '') return true;
      if (typeof val === 'string' && val.trim() === '') return true;
      return false;
    });
  }

  public getMetadata() {
    if (!this.properties?.items?.value) {
      return {};
    }

    const items = this.properties.items.value;
    const metadata = {
      chronic_conditions: [],
      other_chronic_conditions: [],
      allergies: [],
      medications: [],
      surgeries: [],
      diagnosis: [],
    };

    items.forEach((item) => {
      if (item.visible === false || !item.type) {
        return;
      }

      switch (item.name) {
        case 'chronic_conditions':
          if (item.type === 'checkbox-list' && Array.isArray(item.items)) {
            metadata.chronic_conditions = item.items
              .filter((condition) => condition.visible !== false && !!condition.value)
              .map((condition) => {
                const conditionObj: any = {
                  name: condition.title,
                  checked: true,
                };
                if (condition.id) {
                  conditionObj.id = condition.id;
                }
                return conditionObj;
              });
          }
          break;

        case 'other_chronic_conditions':
          if (item.type === 'input-list' && Array.isArray(item.value)) {
            metadata.other_chronic_conditions = item.value
              .map((row) => {
                const dateField = row.find((field) => field.type === 'date');
                const conditionField = row.find((field) => field.title === 'Condition');

                const conditionObj: any = {
                  name: conditionField?.value || '',
                  diagnose_date: this.formatDate(dateField?.value),
                };

                if (conditionField?.id) {
                  conditionObj.id = conditionField.id;
                }

                if (!conditionObj.diagnose_date) {
                  delete conditionObj.diagnose_date;
                }

                return conditionObj;
              })
              .filter((condition) => condition.name);
          }
          break;

        case 'allergies':
          if (item.type === 'input-list' && Array.isArray(item.value)) {
            metadata.allergies = item.value
              .map((row) => {
                const dateField = row.find((field) => field.type === 'date');
                const allergyField = row.find((field) => field.title === 'Allergy');

                const allergyObj: any = {
                  name: allergyField?.value || '',
                  diagnose_date: this.formatDate(dateField?.value),
                };

                if (allergyField?.id) {
                  allergyObj.id = allergyField.id;
                }

                if (!allergyObj.diagnose_date) {
                  delete allergyObj.diagnose_date;
                }

                return allergyObj;
              })
              .filter((allergy) => allergy.name);
          }
          break;

        case 'medications':
          if (item.type === 'table' && Array.isArray(item.value)) {
            metadata.medications = item.value
              .map((medication) => {
                const medObj: any = {
                  start: this.formatDate(medication.Date),
                  name: medication.Medication || '',
                  diagnose_date: this.formatDate(medication.Date),
                  medication: medication.Medication || '',
                  frequency: medication.Freq || '',
                  dosage: medication.Dosage || '',
                  stop: this.formatDate(medication.Stop),
                };

                const id = medication.id || medication.Id;
                if (id) {
                  medObj.id = id;
                }

                if (!medObj.stop) {
                  delete medObj.stop;
                }
                if (!medObj.start) {
                  delete medObj.start;
                }
                if (!medObj.diagnose_date) {
                  delete medObj.diagnose_date;
                }
                return medObj;
              })
              .filter((med) => med.medication);
          }
          break;

        case 'surgeries':
          if (item.type === 'input-list' && Array.isArray(item.value)) {
            metadata.surgeries = item.value
              .map((row) => {
                const dateField = row.find((field) => field.type === 'date');
                const surgeryField = row.find((field) => field.title === 'Surgery');

                const surgeryObj: any = {
                  name: surgeryField?.value || '',
                  diagnose_date: this.formatDate(dateField?.value),
                };

                if (surgeryField?.id) {
                  surgeryObj.id = surgeryField.id;
                }

                if (!surgeryObj.diagnose_date) {
                  delete surgeryObj.diagnose_date;
                }

                return surgeryObj;
              })
              .filter((surgery) => surgery.name);
          }
          break;

        case 'diagnosis':
          if (item.type === 'table' && Array.isArray(item.value)) {
            metadata.diagnosis = item.value
              .map((diagnosis) => ({
                date: this.formatDate(diagnosis.Date),
                code: diagnosis.Code || '',
                diagnosis: diagnosis.Diagnosis || '',
              }))
              .filter((diag) => diag.date && (diag.code || diag.diagnosis));
          }
          break;
      }
    });

    const filteredMetadata = {};
    Object.entries(metadata).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        filteredMetadata[key] = value;
      }
    });

    return filteredMetadata;
  }

  private shouldValidateRequiredInputField(item: any, field: any): boolean {
    if (!field?.required) {
      return false;
    }

    const title = String(field.title || '')
      .trim()
      .toLowerCase();

    if (item?.name === 'other_chronic_conditions') {
      return title === 'condition';
    }

    if (item?.name === 'allergies') {
      return title === 'allergy';
    }

    if (item?.name === 'surgeries') {
      return title === 'surgery';
    }

    return field.required === true;
  }

  private shouldValidateRequiredTableField(item: any, field: any): boolean {
    if (!field?.required) {
      return false;
    }

    if (item?.name !== 'medications') {
      return field.required === true;
    }

    const fieldName = String(field.title || field.name || '')
      .trim()
      .toLowerCase();
    return fieldName === 'medication';
  }

  private isEmptyValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    return false;
  }

  private formatDate(dateValue: string | Date): string {
    if (!dateValue) return '';

    const formattedDate = parseLocalDateString(dateValue);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(formattedDate)) {
      return '';
    }

    const parsedDate = moment(formattedDate, 'YYYY-MM-DD', true);
    if (!parsedDate.isValid() || parsedDate.year() < 1910) {
      return '';
    }

    return formattedDate;
  }
}
